import { HandleType } from '@/enums/handle-type.enum';
import { NodeType } from '@/enums/node-type.enum';
import { FileKind } from '@/designer/designer';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { DEFAULT_GROUP_COLOR } from '@/components/Recipe/Views/NodeGrouping/groups.types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- ModelBaseNodeData used in JSDoc @link
import type { ModelBaseNodeData } from '@/types/nodes/model';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- ParameterView used in JSDoc @link
import type { ParameterView } from '@/components/Recipe/Views/ParameterView/ParameterView';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- FlowGraph used in JSDoc @link
import type { FlowGraph } from '@/components/Recipe/FlowGraph';
import type {
  Blur,
  Channels,
  Invert,
  Levels,
  ListSelector,
  NumberSelector,
  MergeAlpha,
  NodeId,
  Text,
  Prompt,
  Number,
  ExtractVideoFrame,
  Preview,
  PromptConcatenator,
  StringArray,
  Export,
  FigmaCompositor,
  WorkflowOutput,
  Import,
  Toggle,
  Seed,
  Router,
  Resize,
  Crop,
  DilationErosion,
  ParameterConstraint,
  Node as WasmNode,
  MediaIterator,
  Painter,
  MaskExtractor,
  ImportLora,
  Comparison,
  KlingElement,
  Deprecated,
  PointCloudFromDepth,

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- NodeKind used in JSDoc @link
  NodeKind,
} from 'web';

/**
 * @deprecated See {@link BaseNodeData.iteratorInput}.
 */
export type IteratorRunMode = 'parallel'; // TODO: add other run modes here

/**
 * @deprecated See {@link BaseNodeData.iteratorInput}.
 */
export interface IteratorRunModeData {
  runMode: IteratorRunMode;
}

/**
 * @deprecated See {@link BaseNodeData.iteratorInput}.
 */
export type IteratorInput = Record<string, { options: string[] | FileKind[] }> & IteratorRunModeData;

/**
 * @deprecated Use the node specific data in {@link NodeKind} or the generic data in {@link Node} instead.
 */
export interface BaseNodeData {
  /**
   * @deprecated This should be hardcoded in the code based on the node type.
   */
  color: string;
  /**
   * @deprecated This should be hardcoded in the code based on the node type
   * similar to {@link Schema.description} in getParameterTitleAndDescription.
   * Also, the description should be translated.
   */
  description: string;
  /**
   * @deprecated Use {@link FlowGraph.getInputIds} and {@link FlowGraph.getOutputIds} to iterate over the inputs and outputs of a node
   * and specific functionality to replace retrieving the data in {@link Handle}.
   */
  handles: {
    /**
     * @deprecated Use {@link FlowGraph.getInputIds} to iterate over the inputs of a node and specific functionality to replace retrieving the data in {@link Handle}.
     */
    input: Record<string, Handle> | string[];
    /**
     * @deprecated Use {@link FlowGraph.getOutputIds} to iterate over the outputs of a node and specific functionality to replace retrieving the data in {@link Handle}.
     */
    output: Record<string, Handle> | string[];
  };
  /**
   * @deprecated This should not be saved in the node data, it's temporary data.
   */
  initialData?: Record<string, unknown> | null;
  /**
   * @deprecated Use the function {@link FlowGraph.getInputIds} to iterate over the inputs of a node and {@link FlowGraph.evaluateInput} to get the value of an input.
   */
  input?: Record<string, any> | any[];
  /**
   * @deprecated The input should not be saved in the node data, it's temporary data. Instead, compute the input when needed using the function {@link FlowGraph.evaluateInput}.
   */
  iteratorInput?: IteratorInput;
  /**
   * @deprecated Use {@link Node.locked} instead.
   */
  isLocked?: boolean;
  name: string; // TODO: Move to the Node struct since it's shared between all nodes
  /**
   * @deprecated Use the function {@link FlowGraph.getOutputIds} to iterate over the outputs of a node and {@link FlowGraph.evaluateOutput} to get the value of an output.
   */
  output?: Record<string, any> | any[];
  /**
   * @deprecated This should not be saved in the node data, it's temporary data.
   */
  pastedData?: Record<string, unknown>;
  /**
   * @deprecated Use the node specific data instead or {@link FlowGraph.evaluateNode} if the node data is computed from the inputs.
   */
  result?: unknown;
  /**
   * @deprecated Use {@link Node.type} instead.
   */
  type: string;
  /**
   * @deprecated We migrate the data to the newest version, so this should only be used in migration code.
   */
  version?: number;
  /**
   * @deprecated Same as {@link ModelBaseNodeData.model}?
   */
  model?: { name: string };
}

export enum FileSortOrder {
  DateAdded = 'date_added',
  Name = 'name',
}

export interface NodeViewPreferences {
  viewMode?: NodeViewMode;
  sortOrder?: FileSortOrder;
}

/**
 * @deprecated Use {@link FlowGraph.getInputIds} and {@link FlowGraph.getOutputIds} to iterate over the inputs and outputs of a node
 * and specific functionality to replace the data in this interface.
 */
export interface Handle {
  /**
   * @deprecated This should be hardcoded in the code based on the input/output id
   * similar to {@link Schema.description} in getParameterTitleAndDescription.
   * Also, the description should be translated.
   */
  description: string;
  /**
   * @deprecated Use the function {@link FlowGraph.nodeInputType} or {@link FlowGraph.nodeOutputType} to get the type of an input or output.
   */
  format: string;
  /**
   * @deprecated This should be hardcoded in the code based on the input/output id
   * similar to {@link Schema.title} in getParameterTitleAndDescription.
   * Also, the label should be translated.
   */
  label?: string;
  /**
   * @deprecated Use the id given by the functions {@link FlowGraph.getInputIds} and {@link FlowGraph.getOutputIds}.
   */
  id: string;
  /**
   * @deprecated Use the order of the input and output ids given by the function {@link FlowGraph.getInputIds} and {@link FlowGraph.getOutputIds}.
   */
  order: number;
  /**
   * @deprecated Use the function {@link FlowGraph.isInputRequired} to check if an input is required.
   */
  required: boolean;
  /**
   * @deprecated Use the function {@link FlowGraph.nodeInputType} or {@link FlowGraph.nodeOutputType} to get the type of an input or output.
   */
  type?: HandleType;
}

/**
 * @deprecated Not needed since we either iterate over the inputs or outputs of a node
 * using the functions {@link FlowGraph.getInputIds} and {@link FlowGraph.getOutputIds}.
 */
export type HandleSide = 'source' | 'target';

export interface Node extends Omit<WasmNode, 'kind'> {
  id: NodeId;
  type?: string;
  selected?: boolean;
  position: { x: number; y: number };
  width?: number;
  height?: number;
  /**
   * Populated with the specific node data in {@link NodeKind} and the deprecated data in {@link BaseNodeData}.
   */
  data: BaseNodeData;
  createdAt?: string;
  /**
   * @deprecated Use the check {@link Node.type} === 'custommodelV2' to check if a node is a model node.
   */
  isModel?: boolean;
  parentId: string | undefined;
  /** First persisted `data.name` before the user renamed the node (canvas or design app). */
  originalName?: string;
}

export type EdgeId = string;

export interface Edge {
  id: EdgeId;
  source: NodeId;
  target: NodeId;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  /**
   * @deprecated See specific data
   */
  data?: {
    /**
     * @deprecated Use the functions {@link FlowGraph.nodeOutputType}
     * to get the type of an output and from that infer the color.
     */
    sourceColor?: string;
    /**
     * @deprecated Use the functions {@link FlowGraph.nodeInputType}
     * to get the type of an input and from that infer the color.
     */
    targetColor?: string;
    /**
     * @deprecated Use the functions {@link FlowGraph.nodeOutputType} with the source node id and source handle id.
     */
    sourceHandleType?: string;
    /**
     * @deprecated Use the functions {@link FlowGraph.nodeInputType} with the target node id and target handle id.
     */
    targetHandleType?: string;
  };
  /**
   * @deprecated Not needed?
   */
  type?: string;
  selected?: boolean;
}

export type ParameterInfo = ParameterConstraint & {
  title: string;
  description: string;
};

/**
 * @deprecated See the specific data for how to get it.
 */
export interface Schema extends Record<string, unknown> {
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} instead.
   */
  title: string;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} instead.
   */
  description: string;
  /**
   * @deprecated Use the function {@link ParameterView.isParameterExposed} instead.
   */
  exposed?: boolean;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterIds} for getting the parameter ids in the correct order
   */
  order: number;
  /**
   * @deprecated Use the function {@link FlowGraph.isInputRequired}
   */
  required: boolean;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} for getting the constraint {@link ParameterConstraint})
   */
  type:
    | 'integer'
    | 'number'
    | 'string'
    | 'text'
    | 'seed'
    | 'input'
    | 'input-integer'
    | 'input-float'
    | 'input-number'
    | 'array'
    | 'enum'
    | 'boolean'
    | 'fal_image_size'
    | 'file';
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} for getting the constraint {@link ParameterConstraint})
   */
  min?: number;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} for getting the constraint {@link ParameterConstraint})
   */
  max?: number;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterInfo} for getting the constraint {@link ParameterConstraint})
   */
  options?: string[];
}

/**
 * @deprecated Use the function {@link ParameterView.getParameterIds} to iterate over the parameters
 */
export interface NodeDataWithParams extends BaseNodeData {
  /**
   * @deprecated Use the function {@link ParameterView.getParameterIds} to iterate over the parameters.
   * For more deprecation information, see {@link Schema}.
   */
  schema: Record<string, Schema>;
  /**
   * @deprecated Use the function {@link ParameterView.getParameterIds} to iterate over the parameters
   * and {@link ParameterView.getParameterValue} to get the value of a parameter.
   */
  params: Record<string, any>;
}

export type PreviewData = BaseNodeData & Preview;

export type ImportData = BaseNodeData & Import & NodeViewPreferences;

export type MediaIteratorData = BaseNodeData & MediaIterator & NodeViewPreferences;

export type MergeAlphaData = BaseNodeData & MergeAlpha;

export type InvertData = BaseNodeData & Invert;

export type LevelsData = BaseNodeData & Levels;

export const DEFAULT_LEVELS_DATA: LevelsData = {
  version: 3,
  color: '#000000',
  description: '',
  type: 'levels',
  name: 'Levels',
  handles: {
    input: {
      file: {
        description: 'File',
        type: HandleType.Image,
        format: 'uri',
        order: 0,
        required: true,
        id: 'file',
      },
    },
    output: {
      result: {
        description: 'Result',
        type: HandleType.Image,
        format: 'uri',
        order: 0,
        required: true,
        id: 'result',
      },
    },
  },
  inputNode: undefined,
  options: {
    min: { x: 0, y: 0, z: 0 },
    max: { x: 1, y: 1, z: 1 },
    inverseGamma: { x: 1, y: 1, z: 1 },
  },
};

export type DilationErosionData = BaseNodeData & DilationErosion;

export const DEFAULT_DILATION_EROSION_DATA: DilationErosionData = {
  version: 3,
  color: '#000000',
  description: '',
  type: 'dilation_erosion',
  name: 'Matte grow / shrink',
  handles: {
    input: {
      input: {
        description: 'Input',
        label: 'Input',
        type: HandleType.Mask,
        format: 'uri',
        order: 0,
        required: true,
        id: 'input',
      },
    },
    output: {
      result: {
        description: 'Result',
        label: 'Result',
        type: HandleType.Mask,
        format: 'uri',
        order: 0,
        required: true,
        id: 'result',
      },
    },
  },
  inputNode: undefined,
  size: 1,
};

export type ResizeData = BaseNodeData & Resize;

export type PainterData = BaseNodeData & Painter;

export type MaskExtractorData = BaseNodeData & MaskExtractor;

export type ExtractVideoFrameData = BaseNodeData & ExtractVideoFrame;

export type ChannelsData = BaseNodeData & Channels;

export type BlurData = BaseNodeData & Blur;

export type TextData = BaseNodeData & Text & { height?: number };

export type ImportLoraData = BaseNodeData & ImportLora & NodeViewPreferences;

export type ComparisonData = BaseNodeData & Comparison;

export const DEFAULT_COMPARISON_DATA: ComparisonData = {
  version: 3,
  color: '#000000',
  description: 'Compare two inputs with separate outputs',
  type: 'comparison',
  name: 'Compare',
  handles: {
    input: {
      input_a: {
        id: 'inputA',
        type: HandleType.Any,
        label: 'Input A',
        format: 'uri',
        order: 0,
        required: false,
        description: 'First input (Image or Video)',
      },
      input_b: {
        id: 'inputB',
        type: HandleType.Any,
        label: 'Input B',
        format: 'uri',
        order: 1,
        required: false,
        description: 'Second input (Image or Video)',
      },
    },
    output: {
      out_a: {
        id: 'out_a',
        type: HandleType.Any,
        label: 'Output A',
        format: 'uri',
        order: 0,
        required: false,
        description: 'Pass-through from Input A',
      },
      out_b: {
        id: 'out_b',
        type: HandleType.Any,
        label: 'Output B',
        format: 'uri',
        order: 1,
        required: false,
        description: 'Pass-through from Input B',
      },
    },
  },
  inputA: undefined,
  inputB: undefined,
};

export type PointCloudFromDepthData = BaseNodeData & PointCloudFromDepth;

export const DEFAULT_POINT_CLOUD_FROM_DEPTH_DATA: PointCloudFromDepthData = {
  version: 3,
  color: '#000000',
  description: 'Convert depth image to point cloud',
  type: 'point_cloud_from_depth',
  name: 'Point Cloud',
  handles: {
    input: {
      depth: {
        id: 'depth',
        type: HandleType.Image,
        format: 'uri',
        order: 0,
        required: true,
        description: 'Depth image',
      },
      color: {
        id: 'color',
        type: HandleType.Image,
        format: 'uri',
        order: 1,
        required: false,
        description: 'Color image',
      },
    },
    output: {
      image: {
        id: 'image',
        type: HandleType.Image,
        format: 'uri',
        order: 0,
        required: true,
        description: 'Image',
      },
    },
  },
  colorInput: undefined,
  depthInput: undefined,
  cameraOptions: undefined,
  cameraLocked: false,
  options: { depthScale: 5, minDepth: 0, maxDepth: 1, gammaDepth: 0.5 },
};

export type NumberData = BaseNodeData & Number;

export type PromptData = BaseNodeData & Prompt & { height?: number };

export type PromptConcatenatorData = BaseNodeData & PromptConcatenator & { height?: number };

export type ListSelectorData = BaseNodeData &
  ListSelector & { schema?: { options: Schema }; params?: { options: string[] }; height?: number };

export const DEFAULT_LIST_SELECTOR_ITERATOR_DATA: ListSelectorData = {
  version: 3,
  description: 'Iterate over a list of text values',
  type: 'list_selector',
  name: 'Text Iterator',
  handles: {
    input: {
      options: {
        id: 'd83d338b-f6c1-4ff2-a160-e81d918995fc',
        type: HandleType.Any,
        label: 'Text or Array',
        format: 'any',
        required: false,
        order: 0,
        description: 'Text or array of texts to populate the iterator',
      },
    },
    output: {
      option: {
        id: 'xK9mP2vR8nL4jH7tF5wQ123',
        type: HandleType.Text,
        label: 'Text',
        order: 0,
        format: 'text',
        description: 'Iterator item',
        required: false,
      },
    },
  },
  options: undefined,
  delimiter: '*',
  list: [],
  selected: 0,
  schema: {
    options: {
      order: 0,
      type: 'array',
      title: 'Options',
      exposed: true,
      required: false,
      description: 'Array of elements to populate the list',
    },
  },
  isIterator: true,
  color: '#000000',
};

export const DEFAULT_IMAGE_ITERATOR_DATA: MediaIteratorData = {
  version: 3,
  description: 'Iterate over a list of images',
  type: 'media_iterator',
  name: 'Image Iterator',
  handles: {
    input: {},
    output: {
      file: {
        id: 'image-iterator-output',
        type: HandleType.Image,
        label: 'Image',
        order: 0,
        format: 'uri',
        description: 'The selected image',
        required: false,
      },
    },
  },
  color: '#000000',
  files: { type: 'value', data: { type: 'file_array', value: [] } },
  selectedIndex: 0,
  acceptedFileType: 'image',
};

export const DEFAULT_VIDEO_ITERATOR_DATA: MediaIteratorData = {
  version: 3,
  description: 'Iterate over a list of videos',
  type: 'media_iterator',
  name: 'Video Iterator',
  handles: {
    input: {},
    output: {
      file: {
        id: 'video-iterator-output',
        type: HandleType.Video,
        label: 'Video',
        order: 0,
        format: 'uri',
        description: 'The selected video',
        required: false,
      },
    },
  },
  color: '#000000',
  files: { type: 'value', data: { type: 'file_array', value: [] } },
  selectedIndex: 0,
  acceptedFileType: 'video',
};

export const DEFAULT_LIST_SELECTOR_DATA: ListSelectorData = {
  version: 3,
  description: 'Select an option from a list',
  type: 'list_selector',
  name: 'List',
  handles: {
    input: {
      options: {
        id: 'd83d338b-f6c1-4ff2-a160-e81d918995fc',
        type: HandleType.Array,
        label: 'Options',
        format: 'array',
        required: false,
        order: 0,
        description: 'Array of options to choose from',
      },
    },
    output: {
      option: {
        id: 'xK9mP2vR8nL4jH7tF5wQ123',
        type: HandleType.Text,
        label: 'Text',
        order: 0,
        format: 'text',
        description: 'The selected option',
        required: false,
      },
    },
  },
  options: undefined,
  delimiter: ',',
  list: [],
  selected: 0,
  schema: {
    options: {
      order: 0,
      type: 'array',
      title: 'Options',
      exposed: true,
      required: false,
      description: 'Array of options to choose from',
    },
  },
  isIterator: false,
  color: 'Yambo_Green',
};

export type NumberSelectorData = BaseNodeData & NumberSelector & { schema: { options: Schema } };

export const DEFAULT_NUMBER_SELECTOR_DATA: NumberSelectorData = {
  version: 3,
  color: '#000000',
  description: '',
  type: 'number_selector',
  name: 'Number Selector',
  handles: {
    input: {
      options: {
        description: 'Array of numbers to populate the list',
        type: HandleType.Array,
        label: 'Array',
        format: 'array',
        order: 0,
        required: false,
        id: 'array',
      },
    },
    output: {
      option: {
        id: 'xK9mP2vR8nL4jH7tF5wQ123',
        description: 'The selected option',
        type: HandleType.Number,
        order: 0,
        format: 'text',
        required: false,
      },
    },
  },
  selected: undefined,
  params: { options: [] },
  schema: {
    options: {
      order: 0,
      type: 'array',
      title: 'Options',
      exposed: false,
      required: false,
      description: 'Array of elements to populate the list',
    },
  },
  inputNode: undefined,
};

export type CustomGroupData = BaseNodeData & { width: number; height: number; labelFontSize?: number };

export const DEFAULT_CUSTOM_GROUP_DATA: CustomGroupData = {
  version: 3,
  color: DEFAULT_GROUP_COLOR,
  description: 'Group of nodes',
  type: NodeType.CustomGroup,
  name: 'Group',
  handles: { input: [], output: [] },
  width: 400,
  height: 500,
  labelFontSize: 16,
};

export enum StickyNoteSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export type StickyNoteData = BaseNodeData & {
  /** @deprecated Use `size` instead. */
  fontSize?: number;
  size?: StickyNoteSize;
  color?: string;
  result?: {
    note: string;
  };
};

export type StringArrayData = BaseNodeData & StringArray & { result?: string[]; placeholder?: string; height?: number };

export type CropData = BaseNodeData & Crop;

export type ExportData = BaseNodeData & Export;

export type FigmaCompositorData = BaseNodeData & FigmaCompositor & NodeViewPreferences;

export const DEFAULT_FIGMA_COMPOSITOR_DATA: FigmaCompositorData = {
  version: 3,
  color: '#000000',
  description: 'Reference Figma frames',
  type: 'figma_compositor',
  name: 'Figma',
  handles: {
    input: {},
    output: {
      image: {
        id: 'image',
        description: 'Rendered Figma frame',
        format: 'uri',
        order: 0,
        label: 'Output',
        required: false,
      },
    },
  },
  inputs: [],
  outputImage: undefined,
  options: {
    fileKey: 'Skr4i0qECXwNbf8XjZXUe1',
    nodeId: '1-3',
  },
};

export type WorkflowOutputData = BaseNodeData & WorkflowOutput;

export type ToggleData = BaseNodeData & Toggle;

export type SeedData = BaseNodeData & Seed;

export type RouterData = BaseNodeData & Router;

export type KlingElementData = BaseNodeData & KlingElement;

export const DEFAULT_KLING_ELEMENT_DATA: KlingElementData = {
  version: 3,
  color: '#000000',
  description: 'Provide structured element data for the Kling model',
  type: 'kling_element',
  name: 'Kling Element',
  handles: {
    input: {
      frontal_image_url: {
        id: 'frontal_image_url',
        type: HandleType.Image,
        label: 'Frontal Image',
        format: 'uri',
        order: 0,
        required: true,
        description: 'Frontal image URL',
      },
      reference_image_url1: {
        id: 'reference_image_url1',
        type: HandleType.Image,
        label: 'Reference Image 1',
        format: 'uri',
        order: 1,
        required: true,
        description: 'Reference image URL',
      },
      reference_image_url2: {
        id: 'reference_image_url2',
        type: HandleType.Image,
        label: 'Reference Image 2',
        format: 'uri',
        order: 2,
        required: false,
        description: 'Reference image URL',
      },
      reference_image_url3: {
        id: 'reference_image_url3',
        type: HandleType.Image,
        label: 'Reference Image 3',
        format: 'uri',
        order: 3,
        required: false,
        description: 'Reference image URL',
      },
    },
    output: {
      result: {
        id: 'result',
        type: HandleType.KlingElement,
        label: 'Kling Element',
        format: 'uri',
        order: 0,
        required: false,
        description: 'Kling element data',
      },
    },
  },
  frontalImageUrl: undefined,
  referenceImageUrl1: undefined,
  referenceImageUrl2: undefined,
  referenceImageUrl3: undefined,
};

export type HandleAssetName = 'image' | 'style_reference_image' | 'reference_image' | 'video';

export type DeprecatedData = BaseNodeData & Deprecated;
